import { CATALOG_PATTERNS, PATTERN_CATEGORIES } from '@dg-embalajes/catalog';
import { ArrowLeft, Check, ImagePlus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { COPY, patternAssetUrl } from '@/content/copy';
import type { AssetRole, EditorPort, EditorSnapshot } from '@/ports/editor';

export function BackgroundPanel({ editor, snapshot, onImport }: { readonly editor: EditorPort; readonly snapshot: EditorSnapshot; readonly onImport: (role: AssetRole, replace?: boolean) => void }) {
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const patterns = useMemo(() => {
    if (!category) return [];
    const query = search.trim().toLocaleLowerCase('es');
    return CATALOG_PATTERNS.filter(
      (pattern) => pattern.categoryId === category && (!query || pattern.name.toLocaleLowerCase('es').includes(query)),
    );
  }, [category, search]);
  const currentPattern = snapshot.document?.background.kind === 'catalog' ? snapshot.document.background.patternId : null;
  const customBackground = snapshot.document?.background.kind === 'custom' ? snapshot.document.background : null;

  return (
    <div className="panel-stack" data-testid="background-panel">
      <div className="background-primary-actions">
        <button className="background-white" type="button" onClick={() => editor.removeBackground()}>
          <span aria-hidden="true" />
          <div><strong>Quitar fondo</strong><small>Volver a Fondo blanco sin borrar logo ni texto.</small></div>
          {snapshot.document?.background.kind === 'white' ? <Check aria-label="Seleccionado" /> : null}
        </button>
        <button type="button" onClick={() => onImport('background')}><ImagePlus /> Subir fondo propio</button>
      </div>

      {customBackground ? (
        <div className="custom-background-tools">
          <strong>Fondo propio</strong>
          <button type="button" onClick={() => onImport('background', true)}>Reemplazar fondo</button>
          <button type="button" onClick={() => editor.fitCustomBackgroundWidth()}>Ajustar imagen al ancho</button>
          <button type="button" onClick={() => editor.setLocked(customBackground.id, !customBackground.locked)}>
            {customBackground.locked ? 'Desbloquear fondo' : 'Fijar fondo'}
          </button>
        </div>
      ) : null}

      {!category ? (
        <section className="category-picker">
          <div className="panel-copy"><strong>{COPY.chooseBackground}</strong><p>{COPY.backgroundHelp}</p></div>
          <div className="category-grid" data-testid="category-grid">
            {PATTERN_CATEGORIES.map((item) => (
              <button key={item.id} type="button" onClick={() => setCategory(item.id)}>
                <span>{item.name}</span>
                <small>{CATALOG_PATTERNS.filter((pattern) => pattern.categoryId === item.id).length} fondos</small>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className="pattern-picker">
          <div className="pattern-picker__toolbar">
            <button className="back-button" type="button" onClick={() => { setCategory(null); setSearch(''); }}><ArrowLeft /> Categorías</button>
            <strong>{PATTERN_CATEGORIES.find((item) => item.id === category)?.name}</strong>
          </div>
          <label className="search-field">
            <Search aria-hidden="true" size={18} />
            <span className="sr-only">Buscar fondo</span>
            <input value={search} placeholder="Buscar en esta categoría" onChange={(event) => setSearch(event.target.value)} />
          </label>
          <div className="pattern-grid" data-testid="pattern-grid">
            {patterns.map((pattern) => (
              <button
                key={pattern.id}
                type="button"
                className={currentPattern === pattern.id ? 'is-selected' : ''}
                aria-pressed={currentPattern === pattern.id}
                onClick={() => void editor.applyCatalogBackground(pattern.id)}
              >
                <img src={patternAssetUrl(pattern.assetPath, 'thumb')} alt="" loading="lazy" decoding="async" />
                <span>{pattern.name}</span>
                {currentPattern === pattern.id ? <Check aria-label="Seleccionado" /> : null}
              </button>
            ))}
          </div>
        </section>
      )}

      <button className="danger-text-action" type="button" onClick={() => editor.removeBackground()}><Trash2 /> Quitar fondo actual</button>
    </div>
  );
}
